// Pure span-bucketing + stage-failure helpers for the Conversation Inspector.
//
// Extracted from ConversationInspectorPage.jsx (D4) so the logic that decides
// what every pipeline stage displays — and whether a turn is marked failed —
// has a real unit test. It has NO React/DOM dependency; the page imports these
// and keeps only the rendering + the timing-aware `computeStagePill` (which it
// passes into `firstFailedStage`). Sibling of `inspectorConversationPath.js`.

// Map a raw span name to a pipeline-stage bucket, or null when the name alone
// isn't decisive (the page then falls back to a parent-walk before "Other").
export function bucketByName(name) {
  const n = (name || "").trim();
  // CreateEmbeddings fires from RAG search + metadata-filter LLM + LTM
  // dedup — all happen during Context Loading. Bucket there.
  if (/^(indices\.exists|search|index|CreateEmbeddings)$/.test(n)) return "context";
  if (/^(ChatCompletion|TenantLLM\.|Generation)/.test(n)) return "llm";
  if (/^(CompactAndRefine|TokenTextSplitter|SubQuestionQueryEngine|RouterQueryEngine|LLMSingleSelector)/.test(n)) return "tools";
  // obs-completion C4 / C1 — the per-substep and per-tool spans the backend
  // emits are parented to `orchestrator.process_message`, NOT to the LangGraph
  // node they belong to (node spans come from the callback handler and never
  // become the active OTEL context). So neither the direct node match nor the
  // A3 parent-walk can place them, and without these two lines all of them
  // land in "Other" — the Tool Execution stage then reads "No tool calls" on a
  // turn that made eleven, and Context Loading undercounts its substeps.
  if (/^prepare\./.test(n)) return "context";
  if (/^tool\./.test(n)) return "tools";
  // Bare httpx POST/GET spans are NOT bucketed to tools. They cover every
  // outbound call the turn makes — including telemetry egress (OpenObserve
  // ingest, Langfuse) — and a stage fails when ANY span in it has status
  // ERROR, so a failed log-shipping POST was marking Tool Execution FAILED on
  // turns that succeeded. Real tool outcomes now arrive on the `tool.*` spans
  // above, which carry an explicit ERROR status when the tool failed.
  return null;
}

// D1 — hosts we ship telemetry TO. A failed egress POST to one of these says
// nothing about whether the turn served the user, so it must not fail the
// "Other" stage. Conservative by design: an unrecognised host is NOT treated as
// telemetry, so a genuine unattributed integration failure still surfaces.
const TELEMETRY_EGRESS = /(openobserve|langfuse|otel-collector|clickhouse|:5080|:4317|:4318|\/api\/public\/(ingestion|otel))/i;

// True when a span is an outbound call to a telemetry backend. Reads every
// url-ish OTEL attribute, including Langfuse's nested string-encoded bag.
export function isTelemetryEgressSpan(span) {
  const a = (span && span.attributes) || {};
  let bag = a;
  if (typeof a.attributes === "string") {
    try { bag = { ...a, ...JSON.parse(a.attributes) }; } catch { /* keep flat */ }
  }
  const url = bag["http.url"] || bag["url.full"] || bag["http.target"]
    || bag["server.address"] || bag["net.peer.name"] || bag["http.host"] || "";
  return TELEMETRY_EGRESS.test(String(url));
}

// Does a stage have a genuine failure? For the catch-all "other" bucket, a
// telemetry-egress error does NOT count (D1); every other bucket counts any
// ERROR span. `output_safety` + an INACCURATE verdict is also a failure.
export function stageHasGenuineError(stage, turn) {
  if (!stage) return false;
  const errorSpans = (stage.spans || []).filter(s => s && s.status === "ERROR");
  const hasError = stage.id === "other"
    ? errorSpans.some(s => !isTelemetryEgressSpan(s))
    : errorSpans.length > 0;
  if (hasError) return true;
  const verdict = ((turn && turn.verdict) || "").toUpperCase();
  return stage.id === "output_safety" && verdict === "INACCURATE";
}

// First stage that is FAILED — EXCLUDING the "Other" catch-all, whose contents
// we could not attribute to a pipeline stage (F2). `computeStagePill` is passed
// in because it is timing-aware and lives with the page's rendering.
export function firstFailedStage(stages, turn, computeStagePill) {
  if (!Array.isArray(stages)) return null;
  for (const s of stages) {
    // "Other" collects every unclassified outbound call — a probe against a
    // service that isn't deployed, telemetry egress, a background refresh — so
    // an error there says nothing about whether the turn worked.
    if (s && s.id === "other") continue;
    const pill = computeStagePill(s, turn, null);
    if (pill && pill.label === "FAILED") return { stage: s, pill };
  }
  return null;
}
